/**
 * PushBootstrap
 * -------------------------------------------------------------------
 * Componente root para Web Push:
 *  - Registra o service worker (manualmente, para controlar update)
 *  - Ouve mensagens do SW (navegação via click, update disponível)
 *  - Renderiza banners de instalação PWA e permissão de push
 *    para qualquer usuário autenticado (investor, assessor, partner,
 *    mfb, mfa, admin, incorporador).
 *
 * Montado em App.tsx dentro do AuthProvider e BrowserRouter.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Workbox } from "workbox-window";
import { useAuth } from "@/contexts/AuthContext.hook";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { InstallPWAPrompt } from "./InstallPWAPrompt";
import { PushPermissionPrompt } from "./PushPermissionPrompt";
import { useToast } from "@/hooks/use-toast";

export const PushBootstrap = () => {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const push = usePushNotifications();
  const wbRef = useRef<Workbox | null>(null);
  const [updateReady, setUpdateReady] = useState(false);

  // ---------------------------------------------------------------
  // Registro do Service Worker (apenas produção — dev não serve SW)
  // ---------------------------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;
    // Evitar registrar em dev (Vite dev server não serve o /sw.js gerado)
    if (!import.meta.env.PROD) return;

    const wb = new Workbox("/sw.js", { scope: "/" });
    wbRef.current = wb;

    const onWaiting = () => setUpdateReady(true);
    const onControlling = () => {
      // Recarrega para aplicar nova versão depois que o usuário confirma
      window.location.reload();
    };

    wb.addEventListener("waiting", onWaiting);
    wb.addEventListener("controlling", onControlling);

    wb.register().catch((err) => {
      console.error("Falha ao registrar service worker:", err);
    });

    return () => {
      wb.removeEventListener("waiting", onWaiting);
      wb.removeEventListener("controlling", onControlling);
    };
  }, []);

  // ---------------------------------------------------------------
  // Ouvir mensagens do SW (notificationclick → navegar, etc.)
  // ---------------------------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    const handler = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | null;
      if (!data) return;

      if (data.type === "NAVIGATE" && typeof data.url === "string") {
        // Vem de notificationclick quando já há aba aberta
        navigate(data.url);
      }

      if (data.type === "PUSH_SUBSCRIPTION_CHANGE") {
        // Browser rotacionou a subscription — tenta re-subscribe silenciosamente
        void push.subscribe();
      }
    };

    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [navigate, push]);

  // ---------------------------------------------------------------
  // Prompt de update quando nova versão do SW estiver disponível
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!updateReady || !wbRef.current) return;
    const wb = wbRef.current;
    toast({
      title: "Nova versão disponível",
      description: "Clique em \"Atualizar agora\" para aplicar.",
      duration: 15000,
      action: {
        label: "Atualizar agora",
        onClick: async () => {
          // 1) Sinaliza SW em waiting para ativar.
          try {
            wb.messageSkipWaiting();
          } catch {
            // ignore
          }
          // 2) Tenta via registration.waiting diretamente (alguns estados do
          //    Workbox não trackeam `wb.getSW()`; este postMessage é idempotente).
          try {
            const reg = await navigator.serviceWorker.getRegistration();
            reg?.waiting?.postMessage({ type: "SKIP_WAITING" });
          } catch {
            // ignore
          }
          // 3) Aguarda ~500ms para o activate do novo SW começar.
          await new Promise((r) => setTimeout(r, 500));
          // 4) Purga caches do Workbox — crítico para casos em que o SW antigo
          //    estava sem `clients.claim()`, pois o reload sozinho serviria
          //    bundle velho do precache até o próximo cold start.
          try {
            const names = await caches.keys();
            await Promise.all(names.map((n) => caches.delete(n)));
          } catch {
            // ignore — se Cache API falhar, reload abaixo ainda tenta
          }
          // 5) Hard reload com query cache-bust para forçar busca do index.html
          //    atualizado pela network.
          const bust = `${Date.now()}`;
          const url = new URL(window.location.href);
          url.searchParams.set("v", bust);
          window.location.replace(url.toString());
        },
      },
    });
    setUpdateReady(false);
  }, [updateReady, toast]);

  // ---------------------------------------------------------------
  // Renderização dos banners — qualquer user autenticado após auth resolver
  // ---------------------------------------------------------------
  if (loading || !user?.id) return null;
  if (!push.supported) return null;

  // Se já está subscrito, não mostra nada
  if (push.isSubscribed) return null;
  // Se o usuário já negou a permissão explicitamente, não insiste
  if (push.permission === "denied") return null;

  // iOS sem instalar → banner de instalação primeiro
  if (push.platform === "ios" && !push.standalone) {
    return <InstallPWAPrompt platform="ios" />;
  }

  // Android (em browser): banner leve para instalar + prompt de permissão
  // Desktop / PWA standalone → pede permissão direto
  return <PushPermissionPrompt />;
};
